/**
 * PrintablePOSummary
 *
 * Clean PO summary used by the PO Detail modal's Print button. Hidden on
 * screen (`hidden print:block`) and revealed by the @media print rule in
 * `index.css`, which removes every other surface so the printer / "Save as
 * PDF" sees only this subtree.
 *
 * Rendered through a portal into `document.body`, which is load-bearing and
 * not tidiness. The print rule has to take every *other* surface out of
 * layout, and the only selector that can say "everything except this" is a
 * direct-child one — so this block must be a child of body rather than buried
 * in the modal's tree. It also mounts a `po-print-active` marker class on body
 * for the lifetime of the component, so that rule only bites while a PO is
 * actually open; without it, printing any other screen in the app would hide
 * the whole page and emit a blank sheet.
 *
 * The previous approach hid siblings with `visibility: hidden` (which keeps
 * their layout) and pulled this block out of flow with `position: absolute`.
 * A PO that fitted on one page printed fine; anything longer did not. An
 * out-of-flow block cannot be fragmented across pages, so the browser
 * re-applied its origin on every sheet — the header reprinted on top of the
 * continuing lines table, `thead` never repeated, and rows split mid-row.
 * Staying in normal flow is what lets the browser paginate at all.
 *
 * Sections:
 *   1. Header     — PO number, status, issue date, customer + supplier
 *   2. Summary    — currency, total value, terms, delivery, ack metadata
 *   3. Lines      — full table with status / qty / value per line
 *   4. Totals     — line count by status + grand total
 *   5. Remarks    — the order's ERP remarks if present (CR-7363)
 *   6. Notes      — free-text notes if present
 *   7. Footer     — generated-on + generated-by stamp
 *
 * Pure presentational. Receives the already-loaded PO object plus the
 * formatters from the parent so currency / dates render in the user's
 * preferred locale.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { CUSTOMER_REQUIRED_DATE } from '../../config/dateLabels';
import type { PurchaseOrder, PurchaseOrderLine } from '../../lib/api';
import { nextActionFor } from './nextAction';

type FormatCurrency = (value: string, currency?: string, decimals?: boolean | number) => string;
type FormatDate = (dateString: string) => string;
type FormatDateTime = (dateString: string) => string;

interface Props {
  po: PurchaseOrder;
  formatCurrency: FormatCurrency;
  formatDate: FormatDate;
  formatDateTime: FormatDateTime;
  generatedBy?: string | null;
}

// The acknowledgement state in words, for a printed page that has no tooltips
// and no colour. Carried over with the Ack column (EXPEDITING-PARITY PR 1).
const ACK_LABEL: Record<string, string> = {
  new: 'New',
  declined: 'Declined',
  change_proposed_supplier: 'Change Proposed (Supplier)',
  change_proposed_customer: 'Change Proposed (Customer)',
  wip: 'WIP',
  accepted: 'Accepted',
  despatched: 'Dispatched',
  change_declined: 'Change Declined',
  cancelled: 'Cancelled',
  closed: 'Closed',
};

// CR-7412: this ONE map serves both grains — the header statuses above the
// line statuses below. Header: Open / In Progress / Closed. Line: a finished
// line reads Closed. `partially_received` keeps its wording: it is not closed,
// and Received stays the quantity word.
const STATUS_LABEL: Record<string, string> = {
  sent: 'Open',
  in_progress: 'In Progress',
  closed: 'Closed',
  // RETIRED (CR-7412), DORMANT — nothing writes these; kept so a stray row
  // prints a word rather than a raw database string.
  draft: 'Open',
  acknowledged: 'Open',
  delivered: 'Closed',
  pending: 'Pending',
  partially_received: 'Partially Received',
  received: 'Closed',
  // CR-7305: a cancelled line had no entry and printed the raw database
  // string in lower case, the same fault the Lines tab was raised for.
  cancelled: 'Cancelled',
};

/**
 * EXPEDITING-PARITY PR 2, Aaron 17 Sep 2026 — RETIRED. A fifth
 * acknowledge-status table, and it disagreed with the other four: `accepted`
 * read "Accepted" here and "WIP" on every screen, and a HELD line printed
 * whatever it was doing before the hold, with nothing anywhere on the page
 * saying it was held. `statusOf` is the one owner (F6, gate round 1).
 */

/**
 * What a printed line says is happening on it (CR-7305).
 *
 * The Lines tab stopped showing the goods-receipt status and shows the Next
 * Action instead, but the printout kept the old vocabulary, so a held order
 * printed as if nobody were holding it. Same `nextActionFor` as the Lines tab
 * and the PO Lines grid, so the paper and the screen cannot say different
 * things. On a FINISHED line the status is still worth printing, so a
 * received or cancelled line falls through to its own label rather than
 * printing a dash on a closed order. Every other line prints exactly what the
 * screen shows.
 */
const nextActionText = (line: PurchaseOrderLine): string => {
  const { text } = nextActionFor(line);
  if (text !== '-') return text;
  // Only a FINISHED line falls back to its status. `nextActionFor` also
  // returns '-' for a line nobody is waiting on but which is still open, and
  // `get_waiting_on` returns null for acknowledge_status accepted, wip,
  // despatched, declined and cancelled (purchasing/serializers/purchase_order.py:643).
  // Falling back there printed "Pending" under a column headed Next Action,
  // while the Lines tab showed a dash for the same line: the two faults this
  // CR was raised and then reopened for.
  if (line.status === 'received' || line.status === 'cancelled') {
    return STATUS_LABEL[line.status] ?? line.status;
  }
  return text;
};

/** The reason recorded when the line was put on hold, if there is one. */
const holdReason = (line: PurchaseOrderLine): string =>
  line.on_hold_reason_text || line.on_hold_reason_details?.description || '';

export function PrintablePOSummary({
  po,
  formatCurrency,
  formatDate,
  formatDateTime,
  generatedBy,
}: Props) {
  // Scopes the print rule to "a PO summary is mounted". Without this,
  // printing any other screen would match `body > *:not(.printable-po)` and
  // blank the page.
  useEffect(() => {
    document.body.classList.add('po-print-active');
    return () => document.body.classList.remove('po-print-active');
  }, []);

  const lines = po.lines ?? [];
  const lineByStatus = lines.reduce<Record<string, number>>((acc, l) => {
    acc[l.status] = (acc[l.status] ?? 0) + 1;
    return acc;
  }, {});

  const cell = 'border border-black px-2 py-1 text-left align-top';
  const cellNum = 'border border-black px-2 py-1 text-right align-top whitespace-nowrap';

  return createPortal(
    <div className="printable-po hidden print:block">
      {/* 1. Header */}
      <header className="border-b-2 border-black pb-3 mb-4">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold leading-none">Purchase Order</h1>
            <div className="mt-1 text-base">PO #{po.po_number}</div>
          </div>
          <div className="text-right text-xs">
            <div><span className="font-semibold">Status:</span> {STATUS_LABEL[po.status] ?? po.status}</div>
            <div><span className="font-semibold">Issued:</span> {formatDate(po.issue_date)}</div>
            <div><span className="font-semibold">{CUSTOMER_REQUIRED_DATE}:</span> {po.customer_required_date ? formatDate(po.customer_required_date) : '—'}</div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-6 text-xs">
          <div>
            <div className="font-semibold uppercase tracking-wider">Customer</div>
            <div className="mt-0.5">{po.customer_name || '—'}</div>
            {po.delivery_warehouse_details?.name && (
              <div className="mt-0.5">Deliver to: {po.delivery_warehouse_details.name}</div>
            )}
          </div>
          <div>
            <div className="font-semibold uppercase tracking-wider">Supplier</div>
            <div className="mt-0.5">{po.supplier_name || '—'}</div>
            {po.supplier_group_name && (
              <div className="mt-0.5">{po.supplier_group_name}</div>
            )}
          </div>
        </div>
      </header>

      {/* 2. Summary */}
      <section className="mb-4 grid grid-cols-4 gap-x-6 gap-y-2 text-xs">
        <div>
          <div className="font-semibold uppercase tracking-wider">Currency</div>
          <div>{po.currency || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Total Value</div>
          <div>{formatCurrency(po.total_value || '0', po.currency)}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Payment Terms</div>
          <div>{po.payment_terms || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Shipping Terms</div>
          <div>{po.shipping_terms || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Created By</div>
          <div>{po.created_by_name || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Created</div>
          <div>{po.created_at ? formatDateTime(po.created_at) : '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Acknowledged By</div>
          <div>{po.acknowledged_by_name || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Acknowledged</div>
          <div>{po.acknowledged_date ? formatDateTime(po.acknowledged_date) : '—'}</div>
        </div>
      </section>

      {/* 3. Lines */}
      <section className="mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider mb-1">Lines ({lines.length})</h2>
        <table className="w-full text-xs border border-black border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className={cell}>#</th>
              <th className={cell}>Part</th>
              <th className={cell}>Part Description</th>
              {/* CR-7431: HS / customs code, so the printed order carries what
                  the supplier and customs need without a second document. */}
              <th className={cell}>Commodity Code</th>
              <th className={cellNum}>Qty</th>
              <th className={cellNum}>Recd</th>
              <th className={cellNum}>Outstanding</th>
              <th className={cellNum}>Unit Price</th>
              {/* CR-7431: sits between price and total, as on the ERP order. */}
              <th className={cellNum}>Tax %</th>
              <th className={cellNum}>Line Total</th>
              <th className={cell}>Delivery</th>
              {/* CR-7305: the Lines tab's Next Action, not the goods-receipt
                  status. A held order used to print as "Pending" / "New". */}
              <th className={cell}>Next Action</th>
              <th className={cell}>Ack</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td className={cell} colSpan={13}>No line items.</td>
              </tr>
            ) : (
              lines.map((l) => (
                <tr key={l.id}>
                  <td className={cellNum}>{l.line_number}</td>
                  <td className={cell}>{l.part_number || '—'}</td>
                  <td className={cell}>{l.part_description || '—'}</td>
                  <td className={cell}>{l.commodity_code || '—'}</td>
                  <td className={cellNum}>{Number(l.quantity || 0).toLocaleString()}</td>
                  <td className={cellNum}>{Number(l.received_qty || 0).toLocaleString()}</td>
                  <td className={cellNum}>{Number(l.outstanding_qty || 0).toLocaleString()}</td>
                  {/* CR-7431: 4dp. The default is 0dp, which printed a
                      4.0200 unit price as "4". Line Total keeps the default. */}
                  <td className={cellNum}>{formatCurrency(l.unit_price || '0', l.currency || po.currency, 4)}</td>
                  {/* An unset rate is a dash; a genuine 0% prints as 0.000.
                      CR-7363: the ERP's tax code sits under the rate when the
                      line carries its own; a reverse-charge code (e.g. E2) is
                      not charged by the supplier, so the rate is 0 and says why. */}
                  <td className={cellNum}>
                    {l.tax_rate == null || l.tax_rate === '' ? '—' : Number(l.tax_rate).toFixed(3)}
                    {l.erp_tax_code && (
                      <div className="text-[10px] text-gray-700">
                        {l.erp_tax_reverse_charge ? `Reverse charge (${l.erp_tax_code})` : l.erp_tax_code}
                      </div>
                    )}
                  </td>
                  <td className={cellNum}>{formatCurrency(l.total_price || '0', l.currency || po.currency)}</td>
                  <td className={cell}>{l.customer_required_date ? formatDate(l.customer_required_date) : '—'}</td>
                  <td className={cell}>
                    {nextActionText(l)}
                    {l.on_hold && holdReason(l) && (
                      <div className="text-[10px] text-gray-700">{holdReason(l)}</div>
                    )}
                  </td>
                  <td className={cell}>{l.acknowledge_status ? (ACK_LABEL[l.acknowledge_status] ?? l.acknowledge_status) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {/* 4. Totals */}
      <section className="mb-4 flex items-end justify-between text-xs">
        <div>
          <div className="font-semibold uppercase tracking-wider">Lines by status</div>
          <div className="mt-0.5">
            {Object.entries(lineByStatus).length === 0
              ? '—'
              : Object.entries(lineByStatus)
                  .map(([s, n]) => `${STATUS_LABEL[s] ?? s}: ${n}`)
                  .join('  •  ')}
          </div>
        </div>
        <div className="text-right">
          <div className="font-semibold uppercase tracking-wider">Grand Total</div>
          <div className="text-lg font-bold">{formatCurrency(po.total_value || '0', po.currency)}</div>
        </div>
      </section>

      {/* 5. Remarks — CR-7363: as held in the ERP, line breaks kept. */}
      {po.erp_remarks && (
        <section className="mb-4 text-xs">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-1">Remarks</h2>
          <div className="whitespace-pre-wrap border border-black p-2">{po.erp_remarks}</div>
        </section>
      )}

      {/* 6. Notes */}
      {po.notes && (
        <section className="mb-4 text-xs">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-1">Notes</h2>
          <div className="whitespace-pre-wrap border border-black p-2">{po.notes}</div>
        </section>
      )}

      {/* 7. Footer */}
      <footer className="mt-6 pt-2 border-t border-black text-[10px] text-gray-700 flex justify-between">
        <span>Generated {formatDateTime(new Date().toISOString())}{generatedBy ? ` by ${generatedBy}` : ''}</span>
        <span>PO #{po.po_number}</span>
      </footer>
    </div>,
    document.body,
  );
}
